import { useCallback, useEffect, useMemo, useState } from "react";
import { UseFormReturn, useWatch } from "react-hook-form";

import {
  DestinationPropertyFragment,
  DestinationSchemaQueryVariables,
  OperationModeOptionFragment,
  OperationModes,
  SourcePropertyFragment,
  SourceType,
  useDestinationSchemaLazyQuery,
  useFindAvailableOperationsLazyQuery,
  useSourceSchemaLazyQuery,
} from "@/apollo/types";
import { DataFetch_FieldError } from "@/components/primitives/FieldErrorMessage";
import { useMountEffect } from "@/hooks/useMountEffect";

import { FormValues, PropertyMappingFormValue } from "./form";
import {
  autoMapFieldsDynamically,
  autoMapMatchAny,
} from "./lib/auto-mapping/auto-mapping";
import { generateFieldMappings } from "./lib/generateFieldMappings";
import {
  getDestinationPropertySets,
  getSourcePropertySets,
  useDestinationPropertySets,
  useSourcePropertySets,
} from "./lib/usePropertySets";

function shouldAutoMapMatchAny(
  destinationProperties: DestinationPropertyFragment[],
) {
  // Check if automapMatchAny is enabled for any destination property
  // This is the case for spreadsheet destinations such as Google Sheets and Excel.
  // Either all destination properties are automapMatchAny or none of them are.
  return destinationProperties.some((x) => x.automapMatchAny);
}

function getAutoMapSuggestions(
  sourceProperties: ReturnType<typeof useSourcePropertySets>,
  destinationProperties: ReturnType<typeof useDestinationPropertySets>,
) {
  const automapAny = shouldAutoMapMatchAny(destinationProperties.allWritable);

  const autoMappings = automapAny
    ? autoMapMatchAny(
        sourceProperties.unmapped,
        destinationProperties.unmappedWritable,
      )
    : autoMapFieldsDynamically(
        sourceProperties.unmapped,
        destinationProperties.unmappedWritable,
      );
  return autoMappings;
}

export function useMappingData(
  connectionId: string,
  formMethods: UseFormReturn<FormValues>,
) {
  const { setValue, control } = formMethods;

  const selectedObjectType = useWatch<FormValues, "objectTypeOption">({
    name: "objectTypeOption",
    control,
  });
  const selectedOperation = useWatch<FormValues, "operation">({
    name: "operation",
    control,
  });

  const selectedSource = useWatch<FormValues, "source">({
    name: "source",
    control,
  });

  const mappings = useWatch<FormValues, "mappings">({
    name: "mappings",
    control,
  });

  const primaryMapping = useWatch<FormValues, "primaryMapping">({
    name: "primaryMapping",
    control,
  });

  //
  // 2) Load the list of valid sync‐operations for the selected objectType
  //
  const [stableOperations, setStableOperations] = useState<
    OperationModeOptionFragment[]
  >([]);

  const [fetchOperations] = useFindAvailableOperationsLazyQuery({
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      setStableOperations(data.findAllAvailableOperations);
    },
    onError() {
      formMethods.setError("objectTypeOption", {
        type: DataFetch_FieldError,
        message: `Failed to load operations for ${selectedObjectType?.objectName}. Please try again.`,
      });
    },
  });

  //
  // 3) Load source‐model columns
  //
  const [
    fetchSourceProperties,
    { data: sourcePropertiesData, loading: isLoadingSourceProperties },
  ] = useSourceSchemaLazyQuery({
    notifyOnNetworkStatusChange: true,
    onError() {
      formMethods.setError("source", {
        type: DataFetch_FieldError,
        message: "Failed to load source properties. Please try again.",
      });
    },
  });

  //
  // 4) Load destination object properties
  //
  const [
    fetchDestinationProperties,
    {
      data: destinationPropertiesData,
      loading: isLoadingDestinationProperties,
    },
  ] = useDestinationSchemaLazyQuery({
    notifyOnNetworkStatusChange: true,
    onError() {
      formMethods.setError("objectTypeOption", {
        type: DataFetch_FieldError,
        message: "Failed to load destination properties. Please try again.",
      });
    },
  });

  useMountEffect(() => {
    if (!selectedSource) {
      return;
    }
    fetchSourceProperties({
      variables: {
        id: selectedSource.value,
        type: selectedSource.type,
      },
    });
    if (!selectedObjectType) {
      return;
    }
    fetchDestinationProperties({
      variables: {
        input: {
          destinationConnectionId: connectionId,
          objectType: selectedObjectType.objectName,
          operationMode: selectedOperation.operationMode,
          sourceId: selectedSource.value,
          sourceType: selectedSource.type,
        },
      },
    });
  });

  //
  // 5) Compute the property sets, i.e. `unmapped`, `available` etc.
  //
  const sourceProperties = useSourcePropertySets(
    sourcePropertiesData?.sourceSchema,
    mappings,
    primaryMapping,
  );

  const destinationProperties = useDestinationPropertySets(
    destinationPropertiesData?.destinationSchema,
    mappings,
    primaryMapping,
  );

  const applyAutoMappings = useCallback(
    (
      sourceProperties: ReturnType<typeof useSourcePropertySets>,
      destinationProperties: ReturnType<typeof useDestinationPropertySets>,
      existingMappings: PropertyMappingFormValue[],
    ) => {
      const autoMapped = getAutoMapSuggestions(
        sourceProperties,
        destinationProperties,
      );

      const newMappings = generateFieldMappings({
        initialMappings: existingMappings,
        autoMappedProperties: autoMapped,
        requiredDestinationProperties: destinationProperties.required,
      });

      if (newMappings.mappings.length === 0) {
        newMappings.mappings.push({
          sourceProperty: null,
          destinationProperty: null,
        });
      }
      setValue("mappings", newMappings.mappings);
      if (newMappings.primaryMapping?.destinationProperty?.meta) {
        setValue("primaryMapping", {
          sourceProperty:
            newMappings.primaryMapping.sourceProperty ?? undefined,
          destinationProperty: newMappings.primaryMapping.destinationProperty,
        });
      }
    },
    [setValue],
  );

  /**
   * Detect the initial load of model, objectType, source and destination properties.
   * Used for an initial loading state.
   */
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  useEffect(() => {
    if (initialLoadDone) {
      return;
    }
    if (
      selectedSource &&
      selectedObjectType &&
      sourceProperties.all.length > 0 &&
      destinationProperties.all.length > 0
    ) {
      setInitialLoadDone(true);
    }
  }, [
    initialLoadDone,
    selectedSource,
    selectedObjectType,
    sourceProperties,
    destinationProperties,
  ]);

  const autoMappingSuggestions = useMemo(() => {
    return getAutoMapSuggestions(sourceProperties, destinationProperties);
  }, [sourceProperties, destinationProperties]);

  //
  // 6) Handlers
  //

  function autoMapFields(params?: {
    destinationProperties?: DestinationPropertyFragment[];
    sourceProperties?: SourcePropertyFragment[];
  }) {
    let sourceProps = sourceProperties;
    if (params?.sourceProperties) {
      sourceProps = getSourcePropertySets(params.sourceProperties, []);
    }
    let destinationProps = destinationProperties;
    if (params?.destinationProperties) {
      destinationProps = getDestinationPropertySets(
        params.destinationProperties,
        [],
      );
    }

    applyAutoMappings(sourceProps, destinationProps, mappings);
  }

  function initializeMappings(
    sourceProperties: SourcePropertyFragment[],
    destinationProperties: DestinationPropertyFragment[],
  ) {
    const sourceSets = getSourcePropertySets(sourceProperties, []);
    const destinationSets = getDestinationPropertySets(
      destinationProperties,
      [],
    );

    const automapAny = shouldAutoMapMatchAny(destinationSets.allWritable);
    if (automapAny) {
      formMethods.setValue("mappings", [
        {
          sourceProperty: null,
          destinationProperty: null,
        },
      ]);
    } else {
      applyAutoMappings(sourceSets, destinationSets, []);
    }
  }

  return {
    operations: stableOperations,
    sourceProperties,
    destinationProperties,
    isLoadingSourceProperties,
    isLoadingDestinationProperties,
    autoMappingSuggestions,
    initializeMappings,
    loadOperations: async (objectType: string | undefined) => {
      if (!objectType) {
        return;
      }
      const res = await fetchOperations({
        variables: {
          connectionId,
          objectType,
        },
      });
      return {
        operations: res?.data?.findAllAvailableOperations ?? [],
        error: res.error,
      };
    },
    loadSourceProperties: async (id: string, type: SourceType) => {
      const res = await fetchSourceProperties({
        variables: {
          id,
          type,
        },
      });
      return {
        properties: res.data?.sourceSchema ?? [],
        error: res.error,
      };
    },
    loadDestinationProperties: async (
      params: Partial<DestinationSchemaQueryVariables["input"]>,
    ) => {
      const variables: Partial<DestinationSchemaQueryVariables["input"]> = {
        destinationConnectionId: connectionId,
        objectType: selectedObjectType?.objectName,
        operationMode:
          selectedOperation?.operationMode ?? OperationModes.Insert,
        sourceId: selectedSource?.value,
        sourceType: selectedSource?.type,
        ...params,
      };
      if (
        !variables.destinationConnectionId ||
        !variables.objectType ||
        !variables.operationMode ||
        !variables.sourceId ||
        !variables.sourceType
      ) {
        return;
      }
      const res = await fetchDestinationProperties({
        variables: {
          input: {
            destinationConnectionId: variables.destinationConnectionId,
            objectType: variables.objectType,
            operationMode: variables.operationMode,
            sourceId: variables.sourceId,
            sourceType: variables.sourceType,
          },
        },
      });
      return {
        properties: res.data?.destinationSchema ?? [],
        error: res.error,
      };
    },
    refetchSourceProperties: () => {
      if (!selectedSource) {
        return;
      }
      return fetchSourceProperties({
        fetchPolicy: "network-only",
        variables: {
          id: selectedSource.value,
          type: selectedSource.type,
        },
      });
    },
    refetchDestinationProperties: () => {
      if (!selectedObjectType || !selectedSource) {
        return;
      }
      return fetchDestinationProperties({
        fetchPolicy: "network-only",
        variables: {
          input: {
            destinationConnectionId: connectionId,
            objectType: selectedObjectType.objectName,
            operationMode: selectedOperation.operationMode,
            sourceId: selectedSource.value,
            sourceType: selectedSource.type,
          },
        },
      });
    },
    autoMapFields,
    isLoading: !initialLoadDone,
  };
}
